'use client';

import { LoaderPlaceholder } from '@/components/Placeholder';
import { getClassNames } from '@/utils/helpers';
import {
  Banner,
  Button,
  EmptyState,
  IconSticker,
  Table,
} from '@yaradigitallabs/ahua-react';
import type React from 'react';
import type { ProductionLine } from '../utils/types';
import ProductionLineStatus from '../components/production-line-status';

type ProductionLineTableProps = {
  items: Array<ProductionLine>;
  isLoading: boolean;
  error?: string | null;
  handleSelectProductionLine: (line: ProductionLine) => void;
};
const ProductionLineTable: React.FC<ProductionLineTableProps> = ({
  items,
  isLoading,
  error,
  handleSelectProductionLine,
}) => {
  return (
    <div className="px-4 h-[calc(100vh-435px)] md:h-[calc(100vh-320px)] overflow-auto">
      <Table
        className={getClassNames(
          'w-full relative',
          !isLoading && (items || []).length === 0 ? 'h-full' : ''
        )}
      >
        <thead className="sticky top-0 bg-white z-10 shadow-sm">
          <Table.Row>
            <Table.Head className="py-5! px-3! text-nowrap">
              Production line
            </Table.Head>
            <Table.Head className="py-5! px-3! text-nowrap">Status</Table.Head>
            <Table.Head className="py-5! px-3!"></Table.Head>
          </Table.Row>
        </thead>
        <tbody>
          {isLoading && (
            <>
              {Array.from({ length: 3 }).map((_, index) => (
                <Table.Row className="border-b-0!" key={`loader_${index}`}>
                  <Table.Cell className="py-5! px-3! text-nowrap">
                    <LoaderPlaceholder />
                  </Table.Cell>
                  <Table.Cell className="py-5! px-3! text-nowrap">
                    <LoaderPlaceholder />
                  </Table.Cell>
                  <Table.Cell className="py-5! px-3! text-nowrap">
                    <LoaderPlaceholder />
                  </Table.Cell>
                </Table.Row>
              ))}
            </>
          )}
          {error && (
            <Table.Row className="border-b-0!">
              <Table.Cell className="text-center py-6" colSpan={3}>
                <Banner colorConcept="destructiveLight" open>
                  <Banner.Middle>
                    <Banner.Body>
                      <Banner.Message size="n">
                        An error occurred while fetching production line list.
                      </Banner.Message>
                    </Banner.Body>
                  </Banner.Middle>
                </Banner>
              </Table.Cell>
            </Table.Row>
          )}
          {!isLoading && !error && (items || []).length === 0 && (
            <Table.Row className="border-b-0!">
              <Table.Cell className="text-center py-6" colSpan={3}>
                <EmptyState
                  captionText=""
                  iconStickerProps={{
                    icon: 'Info',
                    size: 'xl',
                  }}
                  titleText="No production lines"
                >
                  <EmptyState.Start>
                    <IconSticker icon="Info" size="xl" />
                  </EmptyState.Start>
                  <EmptyState.Middle>
                    <EmptyState.Title>No production lines</EmptyState.Title>
                    <EmptyState.Caption>
                      We do not have any production lines available for
                      reporting. Please check back later.
                    </EmptyState.Caption>
                  </EmptyState.Middle>
                </EmptyState>
              </Table.Cell>
            </Table.Row>
          )}
          {!isLoading &&
            !error &&
            items.map(line => (
              <Table.Row key={line.id}>
                <Table.Cell className="leading-none! py-4! px-3!">
                  {`${line.facility_code} - ${line.facility_description}`}
                </Table.Cell>
                <Table.Cell className="leading-none! py-4! px-3!">
                  <ProductionLineStatus status={line.status} />
                </Table.Cell>
                <Table.ActionsCell>
                  <div className="flex justify-end items-center">
                    <Button
                      colorConcept="brand"
                      size="s"
                      variant="ghost"
                      iconTrailing="ChevronRight"
                      onClick={() => handleSelectProductionLine(line)}
                    >
                      Select
                    </Button>
                  </div>
                </Table.ActionsCell>
              </Table.Row>
            ))}
        </tbody>
      </Table>
    </div>
  );
};

export default ProductionLineTable;
